import moment from "moment";
import React from "react";
import { Message } from "components/Atoms/Message";
import { Button } from "semantic-ui-react";
import CultureFlag from "../../../components/Culture/CultureFlag";
import { Loading } from "../../../components/panels";
import SupportQuestionMark from "../../../supportbeacon/SupportQuestionMark";
import { Pagination } from "../../../pagination";
import { type IPagination } from "../../../pagination/model";
import { sanitizeForUri } from "../../../utilities/encodeutils";
import { getDocumentCoreApi } from "utilities/apiUtils";
import { routes } from "utilities/routing/generatedRoutes";
import { getCulture } from "CMS/utils";

interface Props {
  mainArea: server.dto.MainAreas
  bookkeeping: server.dto.BookkeepingSmall
  documentId: number

  onTemplateSelected: (template: server.dto.WordTemplate) => void
}

const FromTemplate = (props: Props) => {
  const [pagination, setPagination] = React.useState<IPagination>({
    total: 0,
    page: 1,
    perPage: 50,
    maxPerPage: 10,
    pageCount: 0
  });
  const [templates, setTemplates] = React.useState<server.dto.WordTemplate[]>([]);
  const [isLoading, setIsLoading] = React.useState<boolean>(false);

  const maintainDocumentTemplateListUrl = routes.wordTemplate_List(props.mainArea).absolute;

  const paginate = async (page: number) => {
    setIsLoading(true);
    const templateResult = await window.CoreApi.WordTemplate.Paginated({
      mainArea: props.mainArea,
      bookkeepingIds: [props.bookkeeping.bookkeepingId],
      visible: "onlyYes",
      eSignReady: "onlyYes",
      perPage: pagination.perPage,
      page
    });
    setIsLoading(false);

    setTemplates(templateResult.data.data);
    setPagination(templateResult.data.pagination);
  };

  return <>
    <div className="flex">
      <div className="grow1"></div>
      <Pagination
        onPaginate={paginate}
        paginationData={pagination}
      />

    </div>
    <div className="grow1 scrollbox-auto">
      {isLoading && <Loading />}
      {!isLoading && <div className="list-with-gaps line-layout">
        <div className="list-with-gaps-body">
          {templates.map((template) => <TemplateRowCard
            mainArea={props.mainArea}
            key={template.wordTemplateId}
            template={template}
            documentId={props.documentId}
            onClick={props.onTemplateSelected}
          />)}</div>
      </div>}
      {!isLoading && templates.length === 0 && <div>
        <div className="margin-bottom-huge">
          <Message type="info">

            <div className="flex">
              <div className="grow1">
                There are no &quot;eSign ready&quot; templates accessible for bookkeeping: <strong>{props.bookkeeping.name}</strong>
              </div>
              <div>
                <SupportQuestionMark articleId={serverReference.supportArticleIds.templatesESignReady} />
              </div>
            </div>
          </Message>
        </div>
        <div className="flex justify-center">
          <a href={maintainDocumentTemplateListUrl} className="button-new tertiary" >Configure templates</a>
        </div>
      </div>}
    </div>

    {!isLoading && templates.length > 0 && <div>
      <Message type="info">
        <div className="flex">
          <div className="grow1">
            You can generate a document with an &quot;eSign ready&quot; template.
          </div>
          <div>
            <a href={maintainDocumentTemplateListUrl}><i className="icon-cog" /></a>
          </div>
        </div>
      </Message>
    </div>}
  </>;
};

export default FromTemplate;

interface RowProps {
  mainArea: server.dto.MainAreas
  template: server.dto.WordTemplate
  documentId: number
  onClick: (template: server.dto.WordTemplate) => void
}

const TemplateRowCard = (props: RowProps) => {
  const { title, lcid, wordTemplateId } = props.template;
  const timeStamp = moment.utc().format("YYYY MM DD");
  const culture = getCulture(lcid);
  const fileName = `${timeStamp} ${title} [${culture.locale}]`;

  const exportUrl = getDocumentCoreApi(props.mainArea).ExportItem.absoluteUrl(
    props.documentId,
    "download",
    encodeURIComponent(sanitizeForUri(fileName)) + ".docx",
    wordTemplateId,
    lcid
  );

  return <div className="card-floating-bg">
    <div className="grow1 flex gap">
      <div><CultureFlag lcid={lcid} /></div>
      <div className="grow1"><h4>{title}</h4></div>
    </div>

    <div style={{ flexShrink: 0 }} >
      <Button color="green" onClick={() => { props.onClick(props.template); }}>Generate PDF</Button>
    </div>

    <div style={{ flexShrink: 0 }}>
      <a
        href={exportUrl}
        className="button-new tertiary"
      >
        Word download
      </a>
    </div>
  </div>;
};
